import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import {
  useLazyGetMembersBySearchForChannelQuery,
  useRemoveChannelMemberMutation,
} from 'app/services/group.service'
import { FixMeLater } from 'types'
import { IChannelMember } from 'types/user.types'

import { useGetRoomInfoMutation, useInviteParticipantMutation } from 'app/services/room.service'

import Members from 'components/members/Members'
import CustomText from 'components/text/CustomText'
import CustomModal from 'components/modals/CustomModal'
import { ERRORS, OTHERS, PLACEHOLDERS, TABS, VIDEO_CALL } from 'locales/locales'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { CHANNEL_MEMBER_LIMIT } from 'utils/constants'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, TeamAndChannelPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'

interface IConversationInfoProps {
  onClose: () => void
}

const initialGeneralInfo = {
  createdBy: '',
  creatorId: '',
  description: '',
  isAppear: false,
  isArchived: false,
  isMuted: false,
  isPrivate: false,
  name: '',
  createdAt: '',
  notifyOnlyMentions: false,
}

const getIsEmail = (email: string) => {
  const emailRegex = /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/
  return emailRegex.test(email)
}

const ConferenceInviteModal = ({ onClose }: IConversationInfoProps) => {
  const { roomId, meetingId } = useParams()
  const { t } = useTranslation()

  const [inviteParticipant] = useInviteParticipantMutation()
  const [getRoomInfo, { data }] = useGetRoomInfoMutation()
  const [removeChannelMember] = useRemoveChannelMemberMutation()
  const [getMembersBySearch] = useLazyGetMembersBySearchForChannelQuery()

  //WILL BE UPDATE
  const [members, setMembers] = useState<IChannelMember[] | FixMeLater[]>([])
  const [membersBySearch, setMembersBySearch] = useState<IChannelMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const isAdmin = isFlagSetIn(
    [TeamAndChannelPermissions.UPDATE_CHANNEL],
    permissions.teamAndChannel,
  )

  const fetchRoomInfo = async () => {
    try {
      const result: FixMeLater = await getRoomInfo({ roomId: roomId })
      setMembers(result.data.members)
    } catch (e) {
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRoomInfo()
  }, [])

  const onSearch = async (text: string) => {
    try {
      const result: FixMeLater = await getMembersBySearch({ roomId, searchName: text })
      const searchMembers = [...result.data.data]
      if (getIsEmail(text)) {
        const hasUser = members.find(({ email }) => email === text)
        if (!hasUser) {
          searchMembers.push({ email: text })
        }
      }
      if (members.length > 0) {
        return setMembersBySearch([
          ...searchMembers.sort((member) => {
            if (members?.some((m) => m._id === member._id)) {
              return 1
            }
            return -1
          }),
        ])
      }
      setMembersBySearch([...searchMembers])
    } catch (e) {
      console.log(e)
    }
  }

  const onAddMember = async (user: IChannelMember) => {
    if (members.length === CHANNEL_MEMBER_LIMIT) {
      return DropdownAlert.ref.show({
        isError: true,
        message: t(ERRORS.MEMBER_LIMIT_EXCEEDED),
      })
    }

    try {
      const { data }: FixMeLater = await inviteParticipant({
        roomId,
        meetingId,
        data: { userId: user._id },
      })
      if (data?.success) {
        setMembers([...members, user])
      }
    } catch (e) {
      console.log(e)
    }
  }

  const onRemoveMember = async (user: IChannelMember, isCancel?: boolean) => {
    try {
      const removedUserFromMembersData = members.filter(
        (member: IChannelMember) => member._id !== user._id,
      )
      if (isCancel) {
        if (data?.success) setMembers([...removedUserFromMembersData])
      } else {
        const { data }: FixMeLater = await removeChannelMember({
          roomId,
          userId: user._id,
        })
        if (data?.success) setMembers([...removedUserFromMembersData])
      }
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <CustomModal
      header={<Header {...data?.general} />}
      title={t(VIDEO_CALL.INVITE_PARTICIPANT)}
      closeModal={onClose}
      loading={isLoading}
      hasMember
      tab={[
        { title: t(TABS.MEMBERS) },
      ]}
      showButtons={false}
    >
      <Members
        data={members}
        isSearch={true}
        searchedData={membersBySearch}
//        isRemove={true}
        isCollapse={true}
        isAssingMode={false}
        isAddMember={true}
        isReadOnlyPermission={false}
        placeholder={t(PLACEHOLDERS.MEMBERS_PLACEHOLDER)}
        isAdmin={true}
        isRoomModerator={true}
        onRemoveUser={onRemoveMember}
        onChangeSearchText={onSearch}
        onSelectMember={onAddMember}
        isUserCardDisplayable={true}
      />
    </CustomModal>
  )
}

export default ConferenceInviteModal

export const Header = ({ name, isPrivate }: { name?: string; isPrivate?: boolean }) => {
  return (
    <Wrapper>
      <StyledText typography="header1">{name}</StyledText>
    </Wrapper>
  )
}

const StyledText = styled(CustomText)`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
